// /newspaper — Newspaper CMS (v1-C)
// Spec: docs/plans/admin-tooling.md §9
//
// Layout:
//   Left panel:  article list (status pill per article) + Featured section button
//   Right panel: article editor (subject, version, markdown textarea, live preview,
//                schedule date-time, Save / Publish / Schedule / Delete actions)
//                OR revision history with Restore button
//                OR Featured (home) editor (markdown + preview + save)
//
// Theme: forensic-terminal — mono for ids/timestamps/versions, no bg-white/bg-black literals.
// No step-up required (spec §9 decision).

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { Button, Field, Input, ErrorChip, PageHeader } from '@awakenedlands/ui';
import { StatusPill } from '@/components/admin/status-pill';
import type { PillStatus } from '@/components/admin/status-pill';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ArticleSummary {
  news_id: number;
  subject: string;
  version: string;
  status: string;
  publish_at: number | null;
  expires_at: number | null;
  updated_at: number;
  time: number;
}

interface ArticleDetail {
  news_id: number;
  subject: string;
  version: string;
  content: string;
  body_markdown: string;
  body_html: string;
  status: string;
  publish_at: number | null;
  expires_at: number | null;
  updated_at: number;
  deleted_at: number | null;
  time: number;
  user_id: number;
}

interface RevisionRow {
  revision_id: number;
  news_id: number;
  edited_by_staff_id: number;
  edited_at: number;
  subject_snapshot: string;
  body_markdown_snapshot: string;
  status_snapshot: string;
  publish_at_snapshot: number | null;
  expires_at_snapshot: number | null;
}

interface FeaturedContent {
  content: string;
  body_markdown: string;
  body_html: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function articleStatusToPill(status: string): PillStatus {
  switch (status) {
    case 'published': return 'published';
    case 'scheduled': return 'scheduled';
    case 'draft':     return 'draft';
    case 'expired':   return 'expired';
    case 'deleted':   return 'missing';
    default:          return 'draft';
  }
}

function fmtTs(ts: number | null): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
}

// ── Inline confirm modal ──────────────────────────────────────────────────────

interface ConfirmModalProps {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmModal({ title, body, confirmLabel, onConfirm, onCancel }: ConfirmModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85">
      <div className="relative w-full max-w-sm mx-4 rounded-sm border border-border bg-panel-elev p-5 shadow-[0_24px_48px_rgba(0,0,0,0.4)] space-y-4">
        <p className="font-semibold text-ink-high text-sm">{title}</p>
        <p className="text-ink-mid text-xs">{body}</p>
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="danger" onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
}

// ── Preview panel ─────────────────────────────────────────────────────────────

interface PreviewPanelProps {
  markdown: string;
  newsId?: number; // if present, use article preview context
}

function PreviewPanel({ markdown }: PreviewPanelProps) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!markdown.trim()) { setHtml(''); return; }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const res = await apiFetch<{ body_html: string }>('/admin/newspaper/preview', {
        method: 'POST',
        body: JSON.stringify({ body_markdown: markdown }),
      });
      if (!cancelled) {
        setHtml(res.ok ? (res.data.body_html ?? '') : '');
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [markdown]);

  return (
    <div className="border border-border rounded-sm bg-panel p-3 min-h-[120px]">
      <p className="text-xs text-ink-low font-mono mb-2">Preview {loading ? '(loading…)' : ''}</p>
      {html
        ? <div className="prose prose-sm prose-invert max-w-none text-ink-high text-sm" dangerouslySetInnerHTML={{ __html: html }} />
        : <span className="text-xs text-ink-low italic">No content</span>
      }
    </div>
  );
}

// ── Featured editor ───────────────────────────────────────────────────────────

function FeaturedEditor({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-newspaper-featured'],
    queryFn: async () => {
      const res = await apiFetch<FeaturedContent>('/admin/newspaper/featured');
      if (!res.ok) throw new Error('Failed to load featured content');
      return res.data;
    },
  });

  const [markdown, setMarkdown] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (data && !initialized) {
      setMarkdown(data.body_markdown ?? '');
      setInitialized(true);
    }
  }, [data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch('/admin/newspaper/featured', {
        method: 'PUT',
        body: JSON.stringify({ body_markdown: markdown }),
      });
      if (!res.ok) throw new Error('Save failed');
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-featured'] });
    },
  });

  return (
    <div className="space-y-4" data-testid="featured-editor">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-ink-high text-sm">Featured (home) content</p>
        <Button variant="ghost" className="text-xs px-3 h-8" onClick={onClose}>Back to list</Button>
      </div>

      {isLoading && <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />}
      {isError && <ErrorChip>Failed to load featured content</ErrorChip>}

      {!isLoading && (
        <>
          <Field label="Markdown source">
            <textarea
              data-testid="featured-markdown-input"
              className="w-full h-40 rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high font-mono resize-y"
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
            />
          </Field>
          <PreviewPanel markdown={markdown} />
          <div className="flex gap-2">
            <Button
              data-testid="featured-save-btn"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save Featured'}
            </Button>
            {saveMutation.isError && (
              <ErrorChip data-testid="featured-save-error">Save failed</ErrorChip>
            )}
            {saveMutation.isSuccess && (
              <span className="text-xs text-[--color-status-ok] font-mono">Saved</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Revisions panel ───────────────────────────────────────────────────────────

interface RevisionsPanelProps {
  newsId: number;
  onRestored: () => void;
}

function RevisionsPanel({ newsId, onRestored }: RevisionsPanelProps) {
  const queryClient = useQueryClient();
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-newspaper-revisions', newsId],
    queryFn: async () => {
      const res = await apiFetch<{ revisions: RevisionRow[] }>(`/admin/newspaper/${newsId}/revisions`);
      if (!res.ok) throw new Error('Failed to load revisions');
      return res.data;
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (revisionId: number) => {
      const res = await apiFetch(`/admin/newspaper/${newsId}/restore/${revisionId}`, { method: 'POST' });
      if (!res.ok) throw new Error('Restore failed');
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-article', newsId] });
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-revisions', newsId] });
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-list'] });
      setRestoreTarget(null);
      onRestored();
    },
  });

  if (isLoading) return <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />;
  if (isError) return <ErrorChip>Failed to load revisions</ErrorChip>;

  const revisions = data?.revisions ?? [];

  return (
    <div className="space-y-2" data-testid="revisions-panel">
      <p className="text-xs text-ink-low font-mono">Revision history ({revisions.length})</p>
      {revisions.length === 0 && <p className="text-xs text-ink-low italic">No revisions yet</p>}
      {revisions.map((rev) => (
        <div
          key={rev.revision_id}
          data-testid={`rev-row-${rev.revision_id}`}
          className="flex items-center justify-between gap-2 border border-border rounded-sm bg-panel p-2 text-xs font-mono"
        >
          <div className="space-y-0.5 min-w-0">
            <p className="text-ink-high truncate">{rev.subject_snapshot}</p>
            <p className="text-ink-low">
              rev <span className="text-ink-mid">#{rev.revision_id}</span>
              {' · '}{fmtTs(rev.edited_at)}
              {' · '}staff <span className="text-ink-mid">{rev.edited_by_staff_id}</span>
            </p>
          </div>
          <Button
            variant="secondary"
            className="text-xs px-3 h-8"
            data-testid={`restore-btn-${rev.revision_id}`}
            onClick={() => setRestoreTarget(rev.revision_id)}
          >
            Restore
          </Button>
        </div>
      ))}

      {restoreTarget !== null && (
        <ConfirmModal
          title="Restore revision?"
          body={`This will restore revision #${restoreTarget} and capture the current state as a new revision.`}
          confirmLabel="Restore"
          onConfirm={() => restoreMutation.mutate(restoreTarget)}
          onCancel={() => setRestoreTarget(null)}
        />
      )}
      {restoreMutation.isError && <ErrorChip>Restore failed</ErrorChip>}
    </div>
  );
}

// ── Article editor ────────────────────────────────────────────────────────────

interface ArticleEditorProps {
  newsId: number;
  onClose: () => void;
}

type EditorView = 'edit' | 'revisions';

function ArticleEditor({ newsId, onClose }: ArticleEditorProps) {
  const queryClient = useQueryClient();
  const [view, setView] = useState<EditorView>('edit');
  const [subject, setSubject] = useState('');
  const [version, setVersion] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [scheduleAt, setScheduleAt] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-newspaper-article', newsId],
    queryFn: async () => {
      const res = await apiFetch<{ article: ArticleDetail }>(`/admin/newspaper/${newsId}`);
      if (!res.ok) throw new Error('Failed to load article');
      return res.data;
    },
  });

  useEffect(() => {
    if (data?.article && !initialized) {
      setSubject(data.article.subject);
      setVersion(data.article.version);
      setMarkdown(data.article.body_markdown);
      setInitialized(true);
    }
  }, [data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      const res = await apiFetch(`/admin/newspaper/${newsId}`, {
        method: 'PUT',
        body: JSON.stringify({ subject, body_markdown: markdown, version }),
      });
      if (!res.ok) throw new Error('Save failed');
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-article', newsId] });
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-list'] });
    },
    onError: (e) => { setSaveError(e.message); },
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/admin/newspaper/${newsId}/publish`, { method: 'POST' });
      if (!res.ok) throw new Error('Publish failed');
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-article', newsId] });
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-list'] });
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: async () => {
      if (!scheduleAt) throw new Error('No schedule time set');
      const publishAt = Math.floor(new Date(scheduleAt).getTime() / 1000);
      const res = await apiFetch(`/admin/newspaper/${newsId}/schedule`, {
        method: 'POST',
        body: JSON.stringify({ publish_at: publishAt }),
      });
      if (!res.ok) throw new Error('Schedule failed');
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-article', newsId] });
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-list'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/admin/newspaper/${newsId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-list'] });
      onClose();
    },
  });

  if (isLoading) return <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />;
  if (isError) return <ErrorChip>Failed to load article</ErrorChip>;

  const article = data?.article;

  return (
    <div className="space-y-4" data-testid="article-editor">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-ink-high text-sm font-mono">
            news_id:<span className="text-primary">{newsId}</span>
          </p>
          {article && (
            <StatusPill status={articleStatusToPill(article.status)} />
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant={view === 'edit' ? 'primary' : 'ghost'}
            className="text-xs px-3 h-8"
            onClick={() => setView('edit')}
            data-testid="view-edit-btn"
          >
            Edit
          </Button>
          <Button
            variant={view === 'revisions' ? 'primary' : 'ghost'}
            className="text-xs px-3 h-8"
            onClick={() => setView('revisions')}
            data-testid="view-revisions-btn"
          >
            Revisions
          </Button>
          <Button variant="ghost" className="text-xs px-3 h-8" onClick={onClose}>Back</Button>
        </div>
      </div>

      {view === 'edit' && (
        <div className="space-y-3">
          <Field label="Subject">
            <Input
              data-testid="subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Article subject"
            />
          </Field>
          <Field label="Version">
            <Input
              data-testid="version-input"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g. 1.2"
              className="font-mono"
            />
          </Field>
          <Field label="Markdown body">
            <textarea
              data-testid="markdown-input"
              className="w-full h-48 rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high font-mono resize-y"
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
            />
          </Field>
          <PreviewPanel markdown={markdown} />

          <Field label="Schedule publish at (UTC datetime)">
            <input
              type="datetime-local"
              data-testid="schedule-input"
              className="rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high font-mono"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
            />
          </Field>

          {article && (
            <p className="text-xs text-ink-low font-mono">
              updated: {fmtTs(article.updated_at)}
              {article.publish_at != null && ` · published: ${fmtTs(article.publish_at)}`}
              {article.expires_at != null && ` · expires: ${fmtTs(article.expires_at)}`}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="save-btn"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="secondary"
              data-testid="publish-btn"
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending}
            >
              {publishMutation.isPending ? 'Publishing…' : 'Publish Now'}
            </Button>
            <Button
              variant="secondary"
              data-testid="schedule-btn"
              onClick={() => scheduleMutation.mutate()}
              disabled={scheduleMutation.isPending || !scheduleAt}
            >
              {scheduleMutation.isPending ? 'Scheduling…' : 'Schedule'}
            </Button>
            <Button
              variant="danger"
              data-testid="delete-btn"
              onClick={() => setDeleteConfirm(true)}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>

          {saveError && <ErrorChip data-testid="save-error">{saveError}</ErrorChip>}
          {saveMutation.isSuccess && <span className="text-xs text-[--color-status-ok] font-mono">Saved</span>}
          {publishMutation.isError && <ErrorChip>Publish failed</ErrorChip>}
          {scheduleMutation.isError && <ErrorChip>Schedule failed</ErrorChip>}
          {deleteMutation.isError && <ErrorChip>Delete failed</ErrorChip>}
        </div>
      )}

      {view === 'revisions' && (
        <RevisionsPanel newsId={newsId} onRestored={() => setView('edit')} />
      )}

      {deleteConfirm && (
        <ConfirmModal
          title="Delete article?"
          body="The article will be soft-deleted and removed from the player archive. This can be restored via revision history."
          confirmLabel="Delete"
          onConfirm={() => { setDeleteConfirm(false); deleteMutation.mutate(); }}
          onCancel={() => setDeleteConfirm(false)}
        />
      )}
    </div>
  );
}

// ── Create form ───────────────────────────────────────────────────────────────

interface CreateFormProps {
  onCreated: (newsId: number) => void;
  onCancel: () => void;
}

function CreateForm({ onCreated, onCancel }: CreateFormProps) {
  const [subject, setSubject] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [version, setVersion] = useState('');
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch<{ news_id: number }>('/admin/newspaper', {
        method: 'POST',
        body: JSON.stringify({ subject, body_markdown: markdown, version }),
      });
      if (!res.ok) throw new Error('Create failed');
      return res.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-newspaper-list'] });
      onCreated(data.news_id);
    },
  });

  return (
    <div className="space-y-3" data-testid="create-form">
      <p className="font-semibold text-ink-high text-sm">New article</p>
      <Field label="Subject">
        <Input
          data-testid="create-subject-input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Article subject"
        />
      </Field>
      <Field label="Version">
        <Input
          data-testid="create-version-input"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="e.g. 1"
          className="font-mono"
        />
      </Field>
      <Field label="Markdown body">
        <textarea
          data-testid="create-markdown-input"
          className="w-full h-32 rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high font-mono resize-y"
          value={markdown}
          onChange={(e) => setMarkdown(e.target.value)}
        />
      </Field>
      <PreviewPanel markdown={markdown} />
      <div className="flex gap-2">
        <Button
          data-testid="create-save-btn"
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending || !subject.trim()}
        >
          {createMutation.isPending ? 'Creating…' : 'Create draft'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
      {createMutation.isError && <ErrorChip data-testid="create-error">Create failed</ErrorChip>}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

type PanelView =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'article'; newsId: number }
  | { kind: 'featured' };

export function NewspaperPage() {
  const [panelView, setPanelView] = useState<PanelView>({ kind: 'list' });
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-newspaper-list', statusFilter],
    queryFn: async () => {
      const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const res = await apiFetch<{ articles: ArticleSummary[] }>(`/admin/newspaper${qs}`);
      if (!res.ok) throw new Error('Failed to load articles');
      return res.data;
    },
  });

  const articles = data?.articles ?? [];

  return (
    <div className="space-y-4">
      <PageHeader title="Newspaper CMS" />

      {panelView.kind === 'list' && (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <select
              data-testid="status-filter"
              className="rounded-sm border border-border bg-panel px-2 py-1 text-xs text-ink-high font-mono"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All (excl. deleted)</option>
              <option value="draft">draft</option>
              <option value="scheduled">scheduled</option>
              <option value="published">published</option>
              <option value="expired">expired</option>
              <option value="deleted">deleted</option>
            </select>
            <Button
              className="text-xs px-3 h-8"
              data-testid="create-btn"
              onClick={() => setPanelView({ kind: 'create' })}
            >
              + New article
            </Button>
            <Button
              variant="secondary"
              className="text-xs px-3 h-8"
              data-testid="featured-btn"
              onClick={() => setPanelView({ kind: 'featured' })}
            >
              Featured (home)
            </Button>
          </div>

          {/* List */}
          {isLoading && <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" data-testid="list-loading" />}
          {isError && <ErrorChip>Failed to load articles</ErrorChip>}

          {!isLoading && articles.length === 0 && (
            <p className="text-xs text-ink-low italic" data-testid="empty-list">No articles found.</p>
          )}

          <ul className="space-y-1" data-testid="article-list">
            {articles.map((art) => (
              <li key={art.news_id}>
                <button
                  data-testid={`article-btn-${art.news_id}`}
                  className="w-full flex items-center gap-3 rounded-sm border border-border bg-panel px-3 py-2 text-left hover:bg-panel-elev transition-colors"
                  onClick={() => setPanelView({ kind: 'article', newsId: art.news_id })}
                >
                  <StatusPill status={articleStatusToPill(art.status)} />
                  <span className="flex-1 text-sm text-ink-high truncate">{art.subject}</span>
                  <span className="text-xs text-ink-low font-mono">{fmtTs(art.time)}</span>
                  <span className="text-xs text-ink-low font-mono">#{art.news_id}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {panelView.kind === 'create' && (
        <CreateForm
          onCreated={(id) => setPanelView({ kind: 'article', newsId: id })}
          onCancel={() => setPanelView({ kind: 'list' })}
        />
      )}

      {panelView.kind === 'article' && (
        <ArticleEditor
          newsId={panelView.newsId}
          onClose={() => setPanelView({ kind: 'list' })}
        />
      )}

      {panelView.kind === 'featured' && (
        <FeaturedEditor onClose={() => setPanelView({ kind: 'list' })} />
      )}
    </div>
  );
}
